
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Settings, Share2 } from "lucide-react";
import { GroupMember, Group } from "@/lib/types";
import { JoinGroupDialog } from "./JoinGroupDialog";

interface GroupDetailActionsProps {
  currentMember: GroupMember | null;
  group?: Group;
  onJoinGroup: () => Promise<void>;
  onLeaveGroup: () => Promise<void>;
  onOpenSettings: () => void;
  onOpenCreateChannel: () => void;
  onShareGroup?: () => void;
}

export const GroupDetailActions = ({
  currentMember,
  group,
  onJoinGroup,
  onLeaveGroup,
  onOpenSettings,
  onOpenCreateChannel,
  onShareGroup
}: GroupDetailActionsProps) => {
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [isJoining, setIsJoining] = useState(false);

  const handleShowJoinDialog = () => {
    setShowJoinDialog(true);
  };

  const handleConfirmJoin = async () => {
    setIsJoining(true);
    try {
      await onJoinGroup();
    } catch (error) {
      console.error("Error joining group:", error);
    } finally {
      setIsJoining(false);
    }
  };

  if (!currentMember) {
    return (
      <div className="flex flex-wrap gap-2 justify-end">
        <Button onClick={handleShowJoinDialog} disabled={isJoining}>
          {isJoining ? "Joining..." : "Join Group"}
        </Button>
        
        {group && (
          <JoinGroupDialog
            open={showJoinDialog}
            onOpenChange={setShowJoinDialog}
            group={group}
            onConfirm={handleConfirmJoin}
          />
        )}
        
        {onShareGroup && (
          <Button 
            variant="outline"
            onClick={onShareGroup}
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2 justify-end">
      {currentMember.role === "admin" && (
        <>
          <Button 
            variant="outline" 
            onClick={onOpenSettings}
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button 
            variant="outline"
            onClick={onOpenCreateChannel}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Channel
          </Button>
        </>
      )}
      {onShareGroup && (
        <Button 
          variant="outline"
          onClick={onShareGroup}
        >
          <Share2 className="h-4 w-4 mr-2" />
          Share
        </Button>
      )}
      <Button 
        variant="outline" 
        className="text-destructive hover:bg-destructive/10"
        onClick={onLeaveGroup}
      >
        Leave Group
      </Button>
    </div>
  );
};
